import { createMerchantRemarkOption, deleteMerchantRemarkOption, getMerchantRecordList, getMerchantRemarkOptions, updateMerchantRemarkOption } from "services/MerchantAppRecordsAPIHelper";
import { createModel } from "./BaseModel";
import { COUPON_TYPES, DISPLAY_MERCHANT_RECORD_TYPE, MERCHANT_ADJUST_REWARDS_TYPE, MERCHANT_RECORD_TYPE } from "config/CustomEnums";
import { getListDisplayLastModifiedDate } from "utils/TimeFormatUtil";
import { apiWithResponseHandle } from "./LoadingUtil";
import { createAction } from "utils";


const getInitialState = () => ({
  listDisplayFields: [
    {
      displayName: 'ID',
      fieldName: 'pk',
      orderField: 'pk',
      linked: false,
    },
    {
      displayName: 'Membership ID',
      fieldName: 'membershipId',
      linked: true,
    },
    {
      displayName: 'Type',
      fieldName: 'displayType',
    },
    {
      displayName: 'Coupon ID',
      fieldName: 'displayCouponId',
    },
    {
      displayName: 'Ticket ID',
      fieldName: 'displayTicketId',
    },
    {
      displayName: 'Quantity',
      fieldName: 'quantity',
    },
    {
      displayName: 'Store',
      fieldName: 'displayStoreName',
    },
    {
      displayName: 'Merchant admin',
      fieldName: 'displayAdminName',
    },
    {
      displayName: 'Creation date',
      fieldName: 'listDisplayCreationDate',
      orderField: 'creationDate',
    },
  ],
  pageInfo: {
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  },
  currentLastCursor: '',
  currentPage: 0,
  totalPage: 0,
  totalCount: 0,
  pagedList: [],
  checkedList: [],

  allAdminRemarkOptions: [],
});

const getDisplayType = (merchantAppRecord) => {
  const { recordType, adjustRewardsType } = merchantAppRecord || {};
  if (recordType === MERCHANT_RECORD_TYPE.APPROVE_EARNING_RULES) {
    return `${DISPLAY_MERCHANT_RECORD_TYPE?.[recordType]} - ${DISPLAY_MERCHANT_RECORD_TYPE?.[adjustRewardsType]}`
  }
  const isTicketTypeRecord = merchantAppRecord?.couponTemplate?.couponType === COUPON_TYPES.TICKET
  if (recordType === MERCHANT_RECORD_TYPE.REDEEMED_COUPON && isTicketTypeRecord) {
    return DISPLAY_MERCHANT_RECORD_TYPE.REDEEMED_TICKET
  }
  if (recordType !== MERCHANT_RECORD_TYPE.ADJUST_REWARDS) {
    return DISPLAY_MERCHANT_RECORD_TYPE?.[recordType]
  }
  if ([
    MERCHANT_ADJUST_REWARDS_TYPE.ADD_COUPON,
    MERCHANT_ADJUST_REWARDS_TYPE.REMOVE_COUPON,
  ].includes(adjustRewardsType)) {
    if (isTicketTypeRecord) {
      return adjustRewardsType === MERCHANT_ADJUST_REWARDS_TYPE.ADD_COUPON
        ? DISPLAY_MERCHANT_RECORD_TYPE.ADD_TICKET
        : DISPLAY_MERCHANT_RECORD_TYPE.REMOVE_TICKET
    }
  }
  return DISPLAY_MERCHANT_RECORD_TYPE?.[adjustRewardsType]
}

const getDisplayQuantity = (merchantAppRecord) => {
  const { recordType, adjustRewardsAmount, cashDollar } = merchantAppRecord || {};
  if ([
    MERCHANT_RECORD_TYPE.ADJUST_REWARDS,
    MERCHANT_RECORD_TYPE.APPROVE_EARNING_RULES,
  ].includes(recordType)) {
    return adjustRewardsAmount
  }
  if (recordType === MERCHANT_RECORD_TYPE.REDEEMED_COUPON) {
    return merchantAppRecord?.coupon?.pk ? 1 : 0
  }
  if (recordType === MERCHANT_RECORD_TYPE.USE_CASH_DOLLAR) {
    return cashDollar
  }
  return
}

const getDisplayCouponIds = (merchantAppRecord) => {
  const { couponTemplate, couponIds } = merchantAppRecord || {};
  const isRelatedToTicket = couponTemplate?.couponType === COUPON_TYPES.TICKET
  if (isRelatedToTicket) {
    return {
      displayCouponId: null,
      displayTicketId: (couponIds || []).join(","),
    }
  }
  return {
    displayCouponId: (couponIds || []).join(","),
    displayTicketId: null,
  }
}

const getDisplayMembershipId = (customer) => {
  const { isReset, isTransferred, membershipId } = customer || {};
  if (isReset) {
    return `${membershipId} (Deleted)`
  }
  if (isTransferred) {
    return `${membershipId} (Merged)`
  }
  return membershipId
}
const parseMerchantAppRecord = (merchantAppRecord) => {
  return {
    ...merchantAppRecord,
    displayType: getDisplayType(merchantAppRecord),
    quantity: getDisplayQuantity(merchantAppRecord),
    customerId: merchantAppRecord.customer?.pk,
    customerIsRestricted: merchantAppRecord?.customer?.isReset || merchantAppRecord?.customer?.isTransferred,
    membershipId: getDisplayMembershipId(merchantAppRecord?.customer),
    displayStoreName: `[ID: ${merchantAppRecord?.store?.pk || "-"}] ${merchantAppRecord?.store?.name || "-"}`,
    displayAdminName: `[ID: ${merchantAppRecord?.administrator?.pk || "-"}] ${merchantAppRecord?.administrator?.username || "-"}`,
    ...getDisplayCouponIds(merchantAppRecord),
    listDisplayCreationDate: getListDisplayLastModifiedDate(merchantAppRecord?.creationDate),
  }
};
const parseAdminRemarkOptions = (item) => {
  return {
    pk: item.pk,
    optionName: item.optionName,
  }
}


export default createModel({
  namespace: 'merchantAppRecords',
  states: getInitialState(),
  params: {
    listAPI: getMerchantRecordList,
    dataKey: 'merchantUserRecords',
    parse: (data) => data?.merchantUserRecords?.edges?.map((item) => parseMerchantAppRecord(item.node)),
    objectKey: 'transactionList',
  },
  reducers: {},
  effects: {
    getCurrentPageRemarkOptions: [
      function* ({ payload }, { call, put }) {
        const { page } = payload;
        let afterCursor = "";
        if (page > 1) {
          afterCursor = payload.afterCursor;
        }
        const serviceArgs = [getMerchantRemarkOptions, afterCursor, payload];
        function* onSuccess(data) {
          const adminRemarkOptions = data.adminRemarkTemplates?.edges?.map((item) => parseAdminRemarkOptions(item.node));

          yield put(
            createAction("updateState")({
              allAdminRemarkOptions: adminRemarkOptions
            })
          );
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    createOrUpdateOneRemarkOption: [
      function* ({ payload }, { call, put }) {
        const { pk, optionName } = payload;
        const inputBody = {
          optionName
        }
        let serviceArgs = [createMerchantRemarkOption, inputBody];
        if (pk) {
          inputBody.id = pk;
          serviceArgs = [updateMerchantRemarkOption, inputBody];
        }
        function* onSuccess(data) {
          yield put(createAction("getCurrentPageRemarkOptions")());
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    removeRemarkOptions: [
      function* ({ payload }, { call, put }) {
        const { deleteIds } = payload;
        const inputBody = {
          ids: deleteIds || [],
        }
        const serviceArgs = [deleteMerchantRemarkOption, inputBody];
        function* onSuccess(data) {
          yield put(createAction("getCurrentPageRemarkOptions")());
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ]
  },

})